import * as preact from "preact";
import Sortable from "sortablejs";

import * as modalController from "modalController";
import * as wordExport from "shared/wordExport";
import RemoteUserWordsStore from "stores/RemoteUserWordsStore";
import type { AppStores } from "stores/stores";
import type UserWordStore from "stores/UserWordStore";
import Dialog from "views/Dialog";

type ExportField = {
  displayName: string;
  name: string;
  tooltip?: string;
};

type DelimiterType = "tab" | ";" | "other";

type Props = {
  remoteUserWords: RemoteUserWordsStore;
  stores: AppStores;
};

type State = {
  currentFields: ExportField[];
  delimiterType: DelimiterType;
  excludeExported: boolean;
  otherDelimiter: string;
  previewLines: string[];
  unusedFields: ExportField[];
};

const allFields: ExportField[] = [
  { name: "contextBlanked", displayName: "Context (with blank)" },
  { name: "frequency", displayName: "Word Frequency*" },
  { name: "translation", displayName: "Translation" },
  {
    name: "contextWithOriginal",
    displayName: "Context (with original word)",
  },
  { name: "contextAnkiClozeOriginal", displayName: "Context (Anki cloze)" },
  {
    name: "contextAnkiClozeBoth",
    displayName: "Context (Anki cloze with translation)",
  },
  { name: "dateCreated", displayName: "Date Added" },
  { name: "source", displayName: "Source", tooltip: "Document Title" },
  { name: "sourceUrl", displayName: "Source URL", tooltip: "URL" },
  {
    name: "alternativeWords",
    displayName: "Alternatives",
    tooltip: "Synonyms",
  },
  {
    name: "alternativeTranslations",
    displayName: "Alternative Translations",
  },
  {
    name: "contextWithTranslation",
    displayName: "Context (with translation)",
  },
  { name: "word", displayName: "Original" },
];

const fieldByName = (name: string) =>
  allFields.find((field) => field.name === name);

const delimiterFromSettings = (delimiter?: string): DelimiterType => {
  if (delimiter === ";") {
    return ";";
  }
  if (delimiter && delimiter !== "\t") {
    return "other";
  }
  return "tab";
};

export const show = (
  remoteUserWords: RemoteUserWordsStore,
  stores: AppStores
) => {
  modalController.show(
    <ExportDialog remoteUserWords={remoteUserWords} stores={stores} />,
    { title: "exportWords" }
  );
};

class ExportDialog extends preact.Component<Props, State> {
  private currentFieldsRef = preact.createRef<HTMLUListElement>();
  private unusedFieldsRef = preact.createRef<HTMLUListElement>();
  private currentFieldsSortable?: Sortable;
  private unusedFieldsSortable?: Sortable;

  constructor(props: Props) {
    super(props);

    const exportSettings = props.stores.user.state.value.exportSettings;
    const currentFieldNames = exportSettings?.fields || [
      "contextWithTranslation",
      "word",
    ];
    const currentFields = currentFieldNames
      .map(fieldByName)
      .filter((field): field is ExportField => Boolean(field));
    const currentFieldNameSet = new Set(
      currentFields.map((field) => field.name)
    );

    this.state = {
      currentFields,
      delimiterType: delimiterFromSettings(exportSettings?.delimiter),
      excludeExported: exportSettings?.excludeExported ?? false,
      otherDelimiter:
        exportSettings?.delimiter &&
        exportSettings.delimiter !== ";" &&
        exportSettings.delimiter !== "\t"
          ? exportSettings.delimiter
          : ", ",
      previewLines: [],
      unusedFields: allFields.filter(
        (field) => !currentFieldNameSet.has(field.name)
      ),
    };
  }

  componentDidMount() {
    this.currentFieldsSortable = this.createSortable(this.currentFieldsRef);
    this.unusedFieldsSortable = this.createSortable(this.unusedFieldsRef);
    void this.renderPreview();
  }

  componentWillUnmount() {
    this.currentFieldsSortable?.destroy();
    this.unusedFieldsSortable?.destroy();
  }

  componentDidUpdate(_previousProps: Props, previousState: State) {
    if (
      previousState.currentFields !== this.state.currentFields ||
      previousState.delimiterType !== this.state.delimiterType ||
      previousState.otherDelimiter !== this.state.otherDelimiter
    ) {
      void this.renderPreview();
    }
  }

  private createSortable(ref: preact.RefObject<HTMLUListElement>) {
    if (!ref.current) {
      return undefined;
    }

    return Sortable.create(ref.current, {
      animation: 150,
      group: "exportFields",
      onEnd: this.onDragEnd,
    });
  }

  private fieldNamesFromElement(element?: HTMLUListElement | null) {
    return Array.from(element?.querySelectorAll("li[data-field]") ?? [])
      .map((item) => item.getAttribute("data-field"))
      .filter((name): name is string => Boolean(name));
  }

  private onDragEnd = (event: Sortable.SortableEvent) => {
    const currentFields = this.fieldNamesFromElement(
      this.currentFieldsRef.current
    )
      .map(fieldByName)
      .filter((field): field is ExportField => Boolean(field));
    const unusedFields = this.fieldNamesFromElement(
      this.unusedFieldsRef.current
    )
      .map(fieldByName)
      .filter((field): field is ExportField => Boolean(field));

    // Undo Sortable's DOM move before re-rendering: preact's virtual DOM
    // doesn't know about it, so diffing against the mutated DOM would reuse
    // the wrong <li> nodes and corrupt both lists.
    const { from, item, oldIndex } = event;
    if (oldIndex !== undefined) {
      from.insertBefore(item, from.children[oldIndex] ?? null);
    }

    this.setState({ currentFields, unusedFields });
  };

  private delimiter() {
    if (this.state.delimiterType === "tab") {
      return "\t";
    }
    if (this.state.delimiterType === ";") {
      return ";";
    }
    return this.state.otherDelimiter;
  }

  private getURLQuery() {
    return new URLSearchParams({
      query: JSON.stringify({
        ...this.props.remoteUserWords.selectionStore.getQuery(),
        excludeExported: this.state.excludeExported,
      }),
      fields: JSON.stringify(this.selectedFieldNames()),
      delimiter: this.delimiter(),
    }).toString();
  }

  private selectedFieldNames() {
    return this.state.currentFields.map((field) => field.name);
  }

  private async renderPreview() {
    if (
      this.state.delimiterType === "other" &&
      this.state.otherDelimiter === ""
    ) {
      this.setState({ previewLines: [] });
      return;
    }

    const firstWords = this.props.remoteUserWords.selectionStore.itemIDs.value
      .slice(0, 3)
      .map((wordID) => this.props.remoteUserWords.getWord(wordID))
      .filter((word): word is UserWordStore => Boolean(word));

    for (const word of firstWords) {
      if (!word.hasField("context")) {
        await new Promise<void>((resolve) => {
          word.fetch({
            success: function () {
              resolve();
            },
          });
        });
      }
    }

    const fields = this.selectedFieldNames();
    const delimiter = this.delimiter();
    const previewLines: string[] = [];
    for (const word of firstWords) {
      previewLines.push(
        ...wordExport.formatWord(word.viewJSON.value, fields, delimiter)
      );
    }

    this.setState({
      previewLines,
    });
  }

  private exportFile = () => {
    window.location.href = "/api/export.txt?" + this.getURLQuery();

    this.props.stores.user.patch({
      exportSettings: {
        delimiter: this.delimiter(),
        excludeExported: this.state.excludeExported,
        fields: this.selectedFieldNames(),
      },
    });
  };

  private exportToBrowserTab = (event: Event) => {
    event.preventDefault();
    window.open("/api/export?" + this.getURLQuery(), "_blank");
  };

  private setDelimiterType = (event: Event) => {
    this.setState({
      delimiterType: (event.currentTarget as HTMLInputElement)
        .value as DelimiterType,
    });
  };

  private setOtherDelimiter = (event: Event) => {
    this.setState({
      otherDelimiter: (event.currentTarget as HTMLInputElement).value,
    });
  };

  private setExcludeExported = (event: Event) => {
    this.setState({
      excludeExported: (event.currentTarget as HTMLInputElement).checked,
    });
  };

  private renderField(field: ExportField) {
    return (
      <li
        data-field={field.name}
        draggable
        key={field.name}
        title={field.tooltip}
      >
        {field.displayName}
      </li>
    );
  }

  render() {
    const count = this.props.remoteUserWords.selectionStore.count.value;
    const otherDelimiterInvalid =
      this.state.delimiterType === "other" && this.state.otherDelimiter === "";

    return (
      <Dialog heading={`Exporting ${count} words`}>
        <h3>Drag fields to re-arrange</h3>
        <br />
        <div class="dragFields">
          <strong>Don't export these</strong>
          <ul
            class="connected sortable list"
            id="unusedFields"
            ref={this.unusedFieldsRef}
          >
            {this.state.unusedFields.map((field) => this.renderField(field))}
          </ul>
        </div>

        <div class="dragFields">
          <strong>Export these</strong>
          <ul
            class="connected sortable list"
            id="currentFields"
            ref={this.currentFieldsRef}
          >
            {this.state.currentFields.map((field) => this.renderField(field))}
          </ul>
        </div>

        <div style={{ clear: "both" }}></div>
        <div class="mobileOnly">
          <strong>Delimiter:</strong>
        </div>
        <table>
          <tbody>
            <tr>
              <td class="noMobile">
                <strong>Delimiter:</strong>
              </td>
              <td>
                <label>
                  Tab{" "}
                  <input
                    checked={this.state.delimiterType === "tab"}
                    name="delimiter"
                    onChange={this.setDelimiterType}
                    type="radio"
                    value="tab"
                  />
                </label>
              </td>
              <td>
                <label>
                  Semicolon{" "}
                  <input
                    checked={this.state.delimiterType === ";"}
                    name="delimiter"
                    onChange={this.setDelimiterType}
                    type="radio"
                    value=";"
                  />
                </label>
              </td>
              <td>
                <label>
                  Other{" "}
                  <input
                    checked={this.state.delimiterType === "other"}
                    name="delimiter"
                    onChange={this.setDelimiterType}
                    type="radio"
                    value="other"
                  />
                </label>
              </td>
              <td>
                <input
                  class={[
                    "otherDelimiter",
                    otherDelimiterInvalid ? "invalid" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  disabled={this.state.delimiterType !== "other"}
                  onInput={this.setOtherDelimiter}
                  type="text"
                  value={this.state.otherDelimiter}
                />
              </td>
            </tr>
          </tbody>
        </table>
        <p>
          <label
            class="excludeExportedOption"
            title="Skips words you've already exported — every export marks its words as exported. Words with new contexts since their last export are included again, with only the new contexts."
          >
            <input
              checked={this.state.excludeExported}
              onChange={this.setExcludeExported}
              type="checkbox"
            />{" "}
            Exclude previously exported
          </label>
        </p>

        <h3>Example Output</h3>
        <ul id="exampleOutput">
          {this.state.previewLines.map((line) => (
            <li>
              <pre>{line}</pre>
            </li>
          ))}
        </ul>

        <button
          class="prominent"
          id="doExportButton"
          onClick={this.exportFile}
          style={{ fontSize: "100%", padding: 7, width: "100%" }}
        >
          <i class="icon-download icon-large"></i>
          &nbsp; Export File
        </button>

        <p>
          <small>
            or{" "}
            <a
              class="exportToBrowserTab"
              href="#"
              onClick={this.exportToBrowserTab}
            >
              Export to Browser Tab
            </a>
          </small>
        </p>
        <p>
          <small>
            * Word Frequencies from{" "}
            <a href="http://invokeit.wordpress.com/frequency-word-lists/">
              Invoke IT Limited
            </a>
          </small>
        </p>
      </Dialog>
    );
  }
}
